import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Icon, Input, Stack, Text } from '@chakra-ui/react';
import { PlusIcon, UserGroupIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import api from '@/utils/api';
import { unwrap, StagedAssignees } from '@/pages/courses/api';
import { Course, CourseAssignment, User, WorkspaceGroup } from '@/lib/types';

interface AssignCoursePanelProps {
  course: Pick<Course, 'id' | 'tracking_mode'>;
  workspaceId: string;
  // Fired after the first successful assignment so the parent can reflect
  // the mode lock (assignment rows are learner data, which locks the modes;
  // assigning also stamps shared_at).
  onAssigned?: () => void;
  // When present, new selections are held here instead of being sent to the
  // API. Existing assignment rows (a course unpublished after assignment
  // keeps them) still load and can be removed live either way.
  staged?: { value: StagedAssignees; onChange: (next: StagedAssignees) => void };
}

// Assignment panel: current targets as removable chips, group multi-select
// buttons, and a person search — matching the prototype's "Assign to" card.
// Requires an individually tracked course; live assignment additionally needs
// it published (the API answers 409 otherwise and the error surfaces as-is).
export default function AssignCoursePanel({
  course,
  workspaceId,
  onAssigned,
  staged,
}: AssignCoursePanelProps) {
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [personQuery, setPersonQuery] = useState('');

  const load = useCallback(async () => {
    const res = await api.get(`courses/${course.id}/assignments`);
    setAssignments(unwrap<CourseAssignment[]>(res) ?? []);
  }, [course.id]);

  useEffect(() => {
    Promise.all([
      load(),
      api
        .get(`groups/workspace/${workspaceId}`)
        .then((res) => setGroups(unwrap<WorkspaceGroup[]>(res) ?? [])),
      api
        .get(`workspace/${workspaceId}/users`)
        .then((res) => setUsers((res.data.resource as User[]) ?? [])),
    ]).catch(() => {
      // Groups/users load failures leave the pickers empty; assignment list
      // errors surface through the assign/remove actions themselves.
    });
  }, [load, workspaceId]);

  const assign = async (payload: { user?: User; group?: WorkspaceGroup }) => {
    if (staged) {
      staged.onChange({
        users: payload.user ? [...staged.value.users, payload.user] : staged.value.users,
        groups: payload.group ? [...staged.value.groups, payload.group] : staged.value.groups,
      });
      setPersonQuery('');
      return;
    }
    try {
      const res = await api.post(`courses/${course.id}/assignments`, {
        user_ids: payload.user ? [payload.user.id] : [],
        group_ids: payload.group ? [payload.group.id] : [],
      });
      setAssignments(unwrap<CourseAssignment[]>(res) ?? []);
      setPersonQuery('');
      onAssigned?.();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to assign',
        type: 'error',
      });
    }
  };

  const remove = async (assignment: CourseAssignment) => {
    try {
      await api.delete(`courses/assignments/${assignment.id}`);
      await load();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to remove assignment',
        type: 'error',
      });
    }
  };

  const assignedGroupIds = useMemo(
    () =>
      new Set([
        ...assignments.filter((a) => a.group_id != null).map((a) => a.group_id),
        ...(staged?.value.groups ?? []).map((group) => group.id),
      ]),
    [assignments, staged],
  );
  const assignedUserIds = useMemo(
    () =>
      new Set([
        ...assignments.filter((a) => a.user_id != null).map((a) => a.user_id),
        ...(staged?.value.users ?? []).map((user) => user.id),
      ]),
    [assignments, staged],
  );

  const personResults = useMemo(() => {
    const query = personQuery.trim().toLowerCase();
    if (!query) return [];
    return users
      .filter((user) => !assignedUserIds.has(user.id))
      .filter((user) => {
        const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.toLowerCase();
        return name.includes(query) || (user.email ?? '').toLowerCase().includes(query);
      })
      .slice(0, 5);
  }, [users, personQuery, assignedUserIds]);

  const trackable = course.tracking_mode === 'individual';

  return (
    <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={6}>
      <Text
        fontFamily='heading'
        fontSize='2xs'
        fontWeight='700'
        textTransform='uppercase'
        letterSpacing='0.16em'
        color='fg.muted'
        mb={1}
      >
        Assign to
      </Text>
      <Text fontSize='sm' color='fg.muted' mb={4}>
        {!trackable
          ? 'Assignment needs individual tracking — this course counts completions anonymously.'
          : staged
            ? 'Assigned people and groups get the course under "Assigned to me" the moment you publish. Group membership stays live — new members pick it up automatically.'
            : 'Assigned people and groups see this course under "Assigned to me". Group membership stays live — new members pick it up automatically.'}
      </Text>

      {(assignments.length > 0 ||
        (staged?.value.users.length ?? 0) > 0 ||
        (staged?.value.groups.length ?? 0) > 0) && (
        <Flex gap={2} wrap='wrap' mb={4}>
          {assignments.map((assignment) => (
            <Flex
              key={assignment.id}
              align='center'
              gap={2}
              bg='brand.soft'
              borderWidth='1px'
              borderColor='brand.emphasized'
              borderRadius='full'
              px={3}
              py={1.5}
            >
              {assignment.assignee_type === 'group' && (
                <Icon as={UserGroupIcon} w={3.5} h={3.5} color='brand.fg' />
              )}
              <Text fontSize='sm' color='fg.strong'>
                {assignment.label}
                {assignment.assignee_type === 'group' && ` (${assignment.member_count})`}
              </Text>
              <Icon
                as={XMarkIcon}
                w={3.5}
                h={3.5}
                color='fg.muted'
                cursor='pointer'
                onClick={() => remove(assignment)}
              />
            </Flex>
          ))}
          {/* Staged chips render dashed: picked, but nothing is sent until publish. */}
          {staged?.value.groups.map((group) => (
            <Flex
              key={`staged-group-${group.id}`}
              align='center'
              gap={2}
              borderWidth='1px'
              borderStyle='dashed'
              borderColor='brand.emphasized'
              borderRadius='full'
              px={3}
              py={1.5}
            >
              <Icon as={UserGroupIcon} w={3.5} h={3.5} color='brand.fg' />
              <Text fontSize='sm' color='fg.strong'>
                {group.name} ({group.member_count})
              </Text>
              <Icon
                as={XMarkIcon}
                w={3.5}
                h={3.5}
                color='fg.muted'
                cursor='pointer'
                onClick={() =>
                  staged.onChange({
                    ...staged.value,
                    groups: staged.value.groups.filter((g) => g.id !== group.id),
                  })
                }
              />
            </Flex>
          ))}
          {staged?.value.users.map((user) => (
            <Flex
              key={`staged-user-${user.id}`}
              align='center'
              gap={2}
              borderWidth='1px'
              borderStyle='dashed'
              borderColor='brand.emphasized'
              borderRadius='full'
              px={3}
              py={1.5}
            >
              <Text fontSize='sm' color='fg.strong'>
                {`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email}
              </Text>
              <Icon
                as={XMarkIcon}
                w={3.5}
                h={3.5}
                color='fg.muted'
                cursor='pointer'
                onClick={() =>
                  staged.onChange({
                    ...staged.value,
                    users: staged.value.users.filter((u) => u.id !== user.id),
                  })
                }
              />
            </Flex>
          ))}
        </Flex>
      )}

      {trackable && (
        <>
          <Input
            size='sm'
            maxW='md'
            placeholder='Search people in your workspace …'
            value={personQuery}
            onChange={(e) => setPersonQuery(e.target.value)}
          />
          {personResults.length > 0 && (
            <Stack gap={1} mt={2} maxW='md'>
              {personResults.map((user) => (
                <Flex
                  key={user.id}
                  align='center'
                  gap={2.5}
                  px={2.5}
                  py={2}
                  borderRadius='sm'
                  cursor='pointer'
                  _hover={{ bg: 'bg.subtle' }}
                  onClick={() => assign({ user })}
                >
                  <Icon as={PlusIcon} w={3.5} h={3.5} color='brand.fg' />
                  <Box minW={0}>
                    <Text fontSize='sm' color='fg.strong' truncate>
                      {`${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email}
                    </Text>
                    <Text fontSize='xs' color='fg.muted' truncate>
                      {user.email}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Stack>
          )}

          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.16em'
            color='fg.muted'
            mt={4}
            mb={2.5}
          >
            Groups
          </Text>
          <Flex gap={2} wrap='wrap'>
            {groups
              .filter((group) => !assignedGroupIds.has(group.id))
              .map((group) => (
                <Button
                  key={group.id}
                  variant='outline'
                  size='xs'
                  onClick={() => assign({ group })}
                >
                  + {group.name} ({group.member_count})
                </Button>
              ))}
            {groups.length > 0 &&
              groups.every((group) => assignedGroupIds.has(group.id)) && (
                <Text fontSize='xs' color='fg.muted'>
                  Every group is already assigned.
                </Text>
              )}
          </Flex>

          {staged &&
            (staged.value.users.length > 0 || staged.value.groups.length > 0) && (
              <Text fontSize='xs' color='fg.muted' mt={3}>
                These assignments are sent when you publish the course.
              </Text>
            )}
        </>
      )}
    </Box>
  );
}
